import { Box } from '@mui/system';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { TableComponent } from 'app/shared/components/table-component/TableComponent';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import React, { useCallback, useEffect, useState } from 'react';
import { Translate, getSortState, translate } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';
import { getCompanyDataSummary } from './company.reducer';

export const Company = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();

  const totalItems = useAppSelector(state => state.company.totalItems);
  const loading = useAppSelector(state => state.company.loading);
  const urlParams = new URLSearchParams(props.location.search);

  const isRepresentativeAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.REPRESENTATIVE_ADMIN]));
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'name'), props.location.search)
  );
  const [nameSearch, setNameSearch] = useState<string>(urlParams.get('nameSearch') || '');
  const companyList = useAppSelector(state => state.company.entities);
  const [status, setStatus] = useState(StatusFilter.ACTIVATED);

  const allAdmins = isAdmin || isRepresentativeAdmin || isManufacturerAdmin;

  const headers: IHeaderItem[] = [
    {
      key: 'name',
      sortable: true,
      component: <Translate contentKey="fleetSenseApp.company.name" />,
    },
    {
      key: 'identification',
      sortable: true,
      component: <Translate contentKey="fleetSenseApp.company.identification" />,
    },
    {
      key: 'type',
      sortable: true,
      component: <Translate contentKey="fleetSenseApp.company.type" />,
    },
    {
      key: 'activated',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.company.status" />,
    },
    {
      key: 'logo',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.company.logo" />,
    },
  ];

  const filteredHeaders = !allAdmins ? headers.filter(header => header.key !== 'type' && header.key !== 'activated') : headers;

  const data: IListData[] = [
    { type: 'text', value: 'name' },
    { type: 'text', value: 'identification' },
    { type: 'translateText', value: 'type', onlyAdmin: true, translate: 'fleetSenseApp.CompanyType' },
    { type: 'stateButton', value: 'activated', onlyAdmin: true },
    { type: 'image', value: 'logo' },
    { type: 'editButton', value: 'id' },
  ];

  const searchTypes: ISearchTypes[] = [
    {
      label: translate(isManufacturerAdmin ? 'fleetSenseApp.company.representative.searchLabel' : 'fleetSenseApp.company.searchLabel'),
      placeholder: translate('fleetSenseApp.company.search'),
      value: nameSearch,
      setValue: setNameSearch,
    },
  ];

  const getAllEntities = useCallback(() => {
    dispatch(
      getCompanyDataSummary({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${isRepresentativeAdmin || isManufacturerAdmin ? 'targetCompany.' + paginationState.sort : paginationState.sort},${paginationState.order}`,
        nameSearch,
        status,
      })
    );

    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  }, [paginationState, status]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  useEffect(() => {
    const searchParams = [];
    if (nameSearch !== '') {
      searchParams.push(`nameSearch=${encodeURIComponent(nameSearch)}`);
    }

    const searchQueryString = searchParams.length > 0 ? `&${searchParams.join('&')}` : '';

    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}${searchQueryString}`;

    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  }, [paginationState, nameSearch]);

  const handlePagination = (_, page) =>
    setPaginationState({
      ...paginationState,
      activePage: page,
    });

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  const { match } = props;

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', paddingTop: '16px', textAlign: 'center', padding: '24px' }}>
      <PageHeader
        headingId="company-heading"
        headingDataCy="CompanyHeading"
        contentKey={
          isManufacturerAdmin
            ? 'fleetSenseApp.company.representative.title'
            : allAdmins
            ? 'fleetSenseApp.company.home.title'
            : 'fleetSenseApp.company.home.titleSingle'
        }
        buttonTextKey={isManufacturerAdmin ? 'fleetSenseApp.company.representative.create' : 'fleetSenseApp.company.create'}
        match={match}
        showButton={allAdmins}
      />

      <PageFilters
        setActivePage={setActivePage}
        isAdmin={allAdmins}
        searchTypes={searchTypes}
        status={status}
        setStatus={setStatus}
        showFilters={['filters', 'status']}
      />

      {!loading && (
        <TableComponent listData={data} headers={filteredHeaders} list={companyList} match={match} sortFunction={sort} pagination={paginationState} />
      )}
      {!loading && !companyList.length && <NoData title="empresa" />}
      {loading && <Loading />}
      <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={companyList} />
    </Box>
  );
};

export default Company;
